import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, FormGroup, FormControlLabel, Checkbox,
    List, ListItem, Divider, Typography, Grid, TextField, InputAdornment,
    Paper, Box
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { tagList } from '../../utils/placeTagsOptions';
import {
    setZoneTagFilters,
    zoneTagFiltersSelector,
    zonesSelector,
    zonesListSelector,
    pinnedZonesListSelector,
    NO_TAG_VALUE,
    searchZones
} from '../../features/places/placesSlice';

const ZoneTagFilterModal = ({ open, handleClose }) => {
    const dispatch = useDispatch();
    const savedFilters = useSelector(zoneTagFiltersSelector);
    const zones = useSelector(zonesSelector);
    const zonesList = useSelector(zonesListSelector);
    const pinnedZones = useSelector(pinnedZonesListSelector);
    const [selectedTags, setSelectedTags] = useState([]);
    const [nameSearch, setNameSearch] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [localSearchResults, setLocalSearchResults] = useState([]);

    // Create an extended tag list that includes the NO_TAG option
    const extendedTagList = useMemo(() => [...tagList, NO_TAG_VALUE], []);

    useEffect(() => {
        if (savedFilters) {
            setSelectedTags(savedFilters);
        }
    }, [savedFilters]);

    // Perform local search to update UI immediately
    useEffect(() => {
        if (!nameSearch.trim() || !zones || !zonesList) {
            setLocalSearchResults(zonesList);
            return;
        }

        const searchTerm = nameSearch.trim().toUpperCase();
        const results = [...pinnedZones];

        zonesList.forEach(zoneId => {
            const zone = zones[`${zoneId}`];
            if (zone && zone.zone_name && zone.zone_name.toUpperCase().search(searchTerm) !== -1) {
                if (results.indexOf(zoneId) === -1) {
                    results.push(zoneId);
                }
            }
        });

        setLocalSearchResults(results);
    }, [nameSearch, zones, zonesList, pinnedZones]);

    // Calculate the count of zones for each tag
    const tagCounts = useMemo(() => {
        const counts = {};
        // Initialize all tags with 0
        extendedTagList.forEach(tag => {
            counts[tag] = 0;
        });

        // Only count zones that match the name filter
        const zonesListToUse = isSearching ? localSearchResults : zonesList;

        // Count zones for each tag
        if (zones && zonesListToUse) {
            zonesListToUse.forEach(zoneId => {
                const zone = zones[`${zoneId}`];
                if (zone) {
                    if (!zone.zone_tags || zone.zone_tags.length === 0) {
                        // Count zones with no tags
                        counts[NO_TAG_VALUE] = (counts[NO_TAG_VALUE] || 0) + 1;
                    } else {
                        // Count zones for each tag they have
                        zone.zone_tags.forEach(tag => {
                            counts[tag] = (counts[tag] || 0) + 1;
                        });
                    }
                }
            });
        }
        return counts;
    }, [zones, zonesList, localSearchResults, extendedTagList, isSearching]);

    // Count visible zones based on selected tags and name search
    const visibleZonesCount = useMemo(() => {
        if (!zones) return 0;

        // When searching by name, count the local search results
        if (isSearching) {
            return localSearchResults.length;
        }

        // Regular tag-based filtering when not searching by name
        // If no tags selected, return only pinned zones count
        if (selectedTags.length === 0) return pinnedZones.length;

        // If all tags selected, return all zones
        if (selectedTags.length === extendedTagList.length) return zonesList.length;

        // Otherwise count zones with matching tags
        let matchedZoneIds = new Set(pinnedZones); // Include pinned zones

        zonesList.forEach(zoneId => {
            const zone = zones[`${zoneId}`];
            if (zone) {
                // Handle zones with no tags
                if (!zone.zone_tags || zone.zone_tags.length === 0) {
                    if (selectedTags.includes(NO_TAG_VALUE)) {
                        matchedZoneIds.add(zoneId);
                    }
                } else {
                    // Check if any zone tag matches selected filters
                    const hasMatchingTag = zone.zone_tags.some(tag => selectedTags.includes(tag));
                    if (hasMatchingTag) {
                        matchedZoneIds.add(zoneId);
                    }
                }
            }
        });

        return matchedZoneIds.size;
    }, [zones, zonesList, selectedTags, pinnedZones, extendedTagList, localSearchResults, isSearching]);

    const handleTagToggle = (tag) => {
        // Don't allow tag toggling when in search mode
        if (isSearching) return;

        setSelectedTags(prevTags => {
            if (prevTags.includes(tag)) {
                return prevTags.filter(t => t !== tag);
            } else {
                return [...prevTags, tag];
            }
        });
    };

    const handleSelectAll = () => {
        // Don't allow selection changes when in search mode
        if (isSearching) return;

        setSelectedTags([...extendedTagList]);
    };

    const handleSelectNone = () => {
        // Don't allow selection changes when in search mode
        if (isSearching) return;

        setSelectedTags([]);
    };

    const handleNameSearchChange = (event) => {
        const value = event.target.value;
        setNameSearch(value);
        setIsSearching(value.trim() !== '');
    };

    const handleClearSearch = () => {
        setNameSearch('');
        setIsSearching(false);
    };

    const handleModalClose = () => {
        // Reset search state
        setNameSearch('');
        setIsSearching(false);
        setLocalSearchResults(zonesList);
        handleClose();
    };

    const handleApply = () => {
        if (isSearching) {
            // Directly dispatching the Redux searchZones action
            // This will filter zones and update visibleZonesMap
            dispatch(searchZones(nameSearch));
        } else {
            // When filtering by tags, apply the tag filters
            dispatch(setZoneTagFilters(selectedTags));
        }
        // Reset search state
        setNameSearch('');
        setIsSearching(false);
        setLocalSearchResults(zonesList);
        handleClose();
    };

    // Split tags into two columns, including the NO_TAG option in the second column
    const regularTagList = [...tagList];
    const leftColumnTags = regularTagList.slice(0, Math.ceil(regularTagList.length / 2));
    const rightColumnTags = regularTagList.slice(Math.ceil(regularTagList.length / 2));

    // Tag label with count component
    const TagLabel = ({ tag, count }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <Typography variant="body2">
                {tag}
            </Typography>
            <Typography
                variant="caption"
                sx={{
                    backgroundColor: selectedTags.includes(tag) ? '#e3f2fd' : '#eee',
                    color: selectedTags.includes(tag) ? '#1976d2' : '#666',
                    borderRadius: '10px',
                    padding: '2px 8px',
                    fontWeight: 500,
                    minWidth: '32px',
                    textAlign: 'center',
                    marginLeft: '8px'
                }}
            >
                {count}
            </Typography>
        </div>
    );

    return (
        <Dialog
            open={open}
            onClose={handleModalClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                padding: '16px 24px',
                backgroundColor: '#f9f9f9'
            }}>
                <Typography variant="h6" component="div">
                    {isSearching ? 'Search Zones' : 'Filter Zones by Tag'}
                </Typography>
                <Typography
                    variant="subtitle1"
                    component="div"
                    sx={{
                        color: 'text.secondary',
                        backgroundColor: '#eee',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontSize: '0.9rem',
                        fontWeight: 500
                    }}
                >
                    {visibleZonesCount} zones
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ padding: '24px' }}>
                {/* Name search field */}
                <TextField
                    fullWidth
                    placeholder="Search zones by name"
                    value={nameSearch}
                    onChange={handleNameSearchChange}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: nameSearch && (
                            <InputAdornment position="end">
                                <Button
                                    onClick={handleClearSearch}
                                    size="small"
                                    sx={{ minWidth: 'auto', p: '2px' }}
                                >
                                    Clear
                                </Button>
                            </InputAdornment>
                        ),
                    }}
                />

                {isSearching ? (
                    // Search Results Section
                    <Box sx={{ mt: 2 }}>
                        {localSearchResults.length > 0 ? (
                            <List sx={{
                                maxHeight: '400px',
                                overflow: 'auto',
                                '& .MuiListItem-root': {
                                    borderBottom: '1px solid #f0f0f0',
                                    '&:last-child': {
                                        borderBottom: 'none'
                                    }
                                }
                            }}>
                                {localSearchResults.map(zoneId => {
                                    const zone = zones[`${zoneId}`];
                                    return (
                                        <ListItem
                                            key={zoneId}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '12px 16px',
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5'
                                                }
                                            }}
                                        >
                                            <LocationOnIcon
                                                sx={{
                                                    color: zone?.zone_color || '#CF3827',
                                                    marginRight: '12px'
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {zone?.zone_name}
                                                </Typography>
                                                {zone?.zone_tags && zone.zone_tags.length > 0 && (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        Tags: {zone.zone_tags.join(', ')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        ) : (
                            <Paper
                                elevation={0}
                                sx={{ 
                                        p: 3,
                                        textAlign: 'center',
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: '8px'
                                    }}
                                >
                                    <Typography variant="body1" color="text.secondary">
                                        No zones found matching "{nameSearch}"
                                    </Typography>
                                </Paper>
                        )}
                    </Box>
                ) : (
                    // Tag Selection Section
                    <>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '16px',
                            marginTop: '16px'
                        }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleSelectAll}
                                sx={{
                                    borderRadius: '4px',
                                    minWidth: '110px'
                                }}
                                >
                                    Select All
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleSelectNone}
                                    sx={{
                                        borderRadius: '4px',
                                        minWidth: '110px'
                                    }}
                                >
                                    Select None
                                </Button>
                            </div>
                            <Divider style={{ marginBottom: '16px' }} />

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <FormGroup>
                                        {leftColumnTags.map((tag) => (
                                            <FormControlLabel
                                                key={tag}
                                                control={
                                                    <Checkbox
                                                        checked={selectedTags.includes(tag)}
                                                        onChange={() => handleTagToggle(tag)}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                }
                                                label={<TagLabel tag={tag} count={tagCounts[tag] || 0} />}
                                                sx={{
                                                    margin: '2px 0',
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.9rem',
                                                        width: '100%'
                                                    }
                                                }}
                                            />
                                        ))}
                                    </FormGroup>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormGroup>
                                        {rightColumnTags.map((tag) => (
                                            <FormControlLabel
                                                key={tag}
                                                control={
                                                    <Checkbox
                                                        checked={selectedTags.includes(tag)}
                                                        onChange={() => handleTagToggle(tag)}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                }
                                                label={<TagLabel tag={tag} count={tagCounts[tag] || 0} />}
                                                sx={{
                                                    margin: '2px 0',
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.9rem',
                                                        width: '100%'
                                                    }
                                                }}
                                            />
                                        ))}

                                        {/* Special "No Tag" option */}
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={selectedTags.includes(NO_TAG_VALUE)}
                                                    onChange={() => handleTagToggle(NO_TAG_VALUE)}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 500,
                                                            color: '#666',
                                                            fontStyle: 'italic'
                                                        }}
                                                    >
                                                        No Tag
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            backgroundColor: selectedTags.includes(NO_TAG_VALUE) ? '#e3f2fd' : '#eee',
                                                            color: selectedTags.includes(NO_TAG_VALUE) ? '#1976d2' : '#666',
                                                            borderRadius: '10px',
                                                            padding: '2px 8px',
                                                            fontWeight: 500,
                                                            minWidth: '32px',
                                                            textAlign: 'center',
                                                            marginLeft: '8px',
                                                            fontStyle: 'normal'
                                                        }}
                                                    >
                                                        {tagCounts[NO_TAG_VALUE] || 0}
                                                    </Typography>
                                                </div>
                                            }
                                            sx={{
                                                margin: '2px 0',
                                                marginTop: '8px',
                                                borderTop: '1px dashed #eee',
                                                paddingTop: '8px',
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '0.9rem',
                                                    width: '100%'
                                                }
                                            }}
                                        />
                                    </FormGroup>
                                </Grid>
                            </Grid>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{
                padding: '16px 24px',
                borderTop: '1px solid rgba(0,0,0,0.1)',
                backgroundColor: '#f9f9f9'
            }}>
                <Button
                    onClick={handleModalClose}
                    color="primary"
                    sx={{ borderRadius: '4px' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleApply}
                    color="primary"
                    variant="contained"
                    sx={{
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ZoneTagFilterModal; 